 // ═══════════════════════════════════════════════════════════════════════════
// FARM-BOOKING - CRUD PARCELLES / ZONAGE / IoT
// ═══════════════════════════════════════════════════════════════════════════
// 73+ Papy - Janvier 2026
// Fichier : frontend/src/modules/admin/components/GestionParcelles.jsx
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import {
    MapPin, Cpu, Leaf, Trash2, Edit, Plus, X, Save,
    ChevronDown, ChevronRight, AlertTriangle, CheckCircle,
    Scissors, Link2, Unlink, RefreshCw, Search, Filter
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const GestionParcelles = () => {
    // ═══════════════════════════════════════════════════════════════════════
    // ÉTATS
    // ═══════════════════════════════════════════════════════════════════════
    const [parcelles, setParcelles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedParcelle, setExpandedParcelle] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Modals
    const [showAddCulture, setShowAddCulture] = useState(null); // parcelle_id
    const [showEditCulture, setShowEditCulture] = useState(null); // culture object
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // {type, id, nom}
    const [showDiviser, setShowDiviser] = useState(null); // parcelle object
    const [showAssocierModule, setShowAssocierModule] = useState(null); // parcelle_id
    
    // Données pour les selects
    const [varietes, setVarietes] = useState([]);
    const [familles, setFamilles] = useState([]);
    const [modulesDisponibles, setModulesDisponibles] = useState([]);
    
    // Formulaire ajout culture
    const [formCulture, setFormCulture] = useState({
        famille_id: '',
        variete_id: '',
        date_plantation: new Date().toISOString().split('T')[0],
        type_plantation: 'semis',
        surface_m2: '',
        nb_plants: '',
        notes: ''
    });

    // Formulaire division
    const [formDivision, setFormDivision] = useState([
        { nom: 'Zone A', surface_m2: '' },
        { nom: 'Zone B', surface_m2: '' }
    ]);

    // ═══════════════════════════════════════════════════════════════════════
    // CHARGEMENT INITIAL
    // ═══════════════════════════════════════════════════════════════════════
    useEffect(() => {
        loadParcelles();
        loadFamilles();
    }, []);

    const loadParcelles = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/parcelles-gestion/liste-complete`);
            const data = await res.json();
            if (data.success) {
                setParcelles(data.data);
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Erreur chargement des parcelles');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadFamilles = async () => {
        try {
            const res = await fetch(`${API_BASE}/cultures-gestion/familles`);
            const data = await res.json();
            if (data.success) setFamilles(data.data);
        } catch (err) {
            console.error('Erreur chargement familles:', err);
        }
    };

    const loadVarietes = async (familleId) => {
        try {
            const res = await fetch(`${API_BASE}/cultures-gestion/varietes-kc-select?famille_id=${familleId}`);
            const data = await res.json();
            if (data.success) setVarietes(data.data);
        } catch (err) {
            console.error('Erreur chargement variétés:', err);
        }
    };

    const loadModulesDisponibles = async () => {
        try {
            const res = await fetch(`${API_BASE}/parcelles-gestion/modules/disponibles`);
            const data = await res.json();
            if (data.success) setModulesDisponibles(data.data);
        } catch (err) {
            console.error('Erreur chargement modules:', err);
        }
    };

    // ═══════════════════════════════════════════════════════════════════════
    // HANDLERS
    // ═══════════════════════════════════════════════════════════════════════
    
    // Ajouter une culture
    const handleAddCulture = async () => {
        try {
            const res = await fetch(`${API_BASE}/parcelles-gestion/${showAddCulture}/ajouter-culture`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formCulture)
            });
            const data = await res.json();
            if (data.success) {
                setShowAddCulture(null);
                setFormCulture({
                    famille_id: '',
                    variete_id: '',
                    date_plantation: new Date().toISOString().split('T')[0],
                    type_plantation: 'semis',
                    surface_m2: '',
                    nb_plants: '',
                    notes: ''
                });
                loadParcelles();
            } else {
                alert(data.message);
            }
        } catch (err) {
            alert('Erreur: ' + err.message);
        }
    };

    // Supprimer une culture
    const handleDeleteCulture = async () => {
        if (!showDeleteConfirm) return;
        try {
            const res = await fetch(`${API_BASE}/parcelles-gestion/culture/${showDeleteConfirm.id}`, {
                method: 'DELETE'
            });
            const data = await res.json();
            if (data.success) {
                setShowDeleteConfirm(null);
                loadParcelles();
            } else {
                alert(data.message);
            }
        } catch (err) {
            alert('Erreur: ' + err.message);
        }
    };

    // Associer un module IoT
    const handleAssocierModule = async (moduleId) => {
        try {
            const res = await fetch(`${API_BASE}/parcelles-gestion/${showAssocierModule}/associer-module`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ module_id: moduleId })
            });
            const data = await res.json();
            if (data.success) {
                setShowAssocierModule(null);
                loadParcelles();
            } else {
                alert(data.message);
            }
        } catch (err) {
            alert('Erreur: ' + err.message);
        }
    };

    // Dissocier un module IoT
    const handleDissocierModule = async (parcelleId, moduleId) => {
        if (!confirm('Dissocier ce module IoT de la parcelle ?')) return;
        try {
            const res = await fetch(`${API_BASE}/parcelles-gestion/${parcelleId}/dissocier-module`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ module_id: moduleId })
            });
            const data = await res.json();
            if (data.success) {
                loadParcelles();
            } else {
                alert(data.message);
            }
        } catch (err) {
            alert('Erreur: ' + err.message);
        }
    };

    // Diviser une parcelle
    const handleDiviser = async () => {
        if (!showDiviser) return;
        try {
            const res = await fetch(`${API_BASE}/parcelles-gestion/${showDiviser.id}/diviser`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ zones: formDivision })
            });
            const data = await res.json();
            if (data.success) {
                setShowDiviser(null);
                setFormDivision([
                    { nom: 'Zone A', surface_m2: '' },
                    { nom: 'Zone B', surface_m2: '' }
                ]);
                loadParcelles();
            } else {
                alert(data.message);
            }
        } catch (err) {
            alert('Erreur: ' + err.message);
        }
    };

    // ═══════════════════════════════════════════════════════════════════════
    // FILTRAGE
    // ═══════════════════════════════════════════════════════════════════════
    const parcellesFiltrees = parcelles.filter(p => 
        p.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.nom_complet.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.cultures.some(c => c.variete_nom?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // ═══════════════════════════════════════════════════════════════════════
    // RENDU CARTE PARCELLE
    // ═══════════════════════════════════════════════════════════════════════
    const renderParcelle = (parcelle) => {
        const isExpanded = expandedParcelle === parcelle.id;
        const hasAlerte = parcelle.surface_disponible < 0;
        
        return (
            <div key={parcelle.id} className="bg-white rounded-lg shadow-md overflow-hidden mb-4">
                {/* Header parcelle */}
                <div 
                    className={`p-4 cursor-pointer flex items-center justify-between ${
                        hasAlerte ? 'bg-red-50' : 'bg-gray-50'
                    }`}
                    onClick={() => setExpandedParcelle(isExpanded ? null : parcelle.id)}
                >
                    <div className="flex items-center gap-3">
                        {isExpanded ? 
                            <ChevronDown className="w-5 h-5 text-gray-500" /> : 
                            <ChevronRight className="w-5 h-5 text-gray-500" />
                        }
                        <MapPin className="w-5 h-5 text-green-600" />
                        <div>
                            <h3 className="font-semibold text-gray-900">{parcelle.nom_complet}</h3>
                            <div className="text-sm text-gray-500 flex items-center gap-3">
                                <span>{parcelle.surface_m2} m² total</span>
                                <span className={`${parcelle.surface_disponible >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {parseFloat(parcelle.surface_disponible || 0).toFixed(1)} m² disponible
                                </span>
                                {parcelle.type_abri !== 'aucun' && (
                                    <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">
                                        {parcelle.type_abri}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        {/* Badges cultures */}
                        <div className="flex gap-1">
                            {parcelle.cultures.slice(0, 3).map((c, i) => (
                                <span key={i} className="text-lg" title={c.variete_nom}>
                                    {c.icone || '🌱'}
                                </span>
                            ))}
                            {parcelle.cultures.length > 3 && (
                                <span className="text-sm text-gray-500">+{parcelle.cultures.length - 3}</span>
                            )}
                        </div>
                        
                        {/* Badge IoT */}
                        {parcelle.modules_iot.length > 0 && (
                            <span className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
                                parcelle.modules_iot.some(m => m.etat_connexion === 'online') 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-gray-100 text-gray-600'
                            }`}>
                                <Cpu className="w-3 h-3" />
                                {parcelle.modules_iot.length}
                            </span>
                        )}
                        
                        {/* Actions rapides */}
                        <button
                            onClick={(e) => { e.stopPropagation(); setShowAddCulture(parcelle.id); }}
                            className="p-2 hover:bg-green-100 rounded-lg text-green-600"
                            title="Ajouter une culture"
                        >
                            <Plus className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Contenu étendu */}
                {isExpanded && (
                    <div className="p-4 border-t">
                        {/* Actions parcelle */}
                        <div className="flex gap-2 mb-4">
                            <button
                                onClick={() => setShowAddCulture(parcelle.id)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                            >
                                <Plus className="w-4 h-4" /> Ajouter culture
                            </button>
                            <button
                                onClick={() => { setShowAssocierModule(parcelle.id); loadModulesDisponibles(); }}
                                className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                            >
                                <Link2 className="w-4 h-4" /> Associer ESP32
                            </button>
                            <button
                                onClick={() => { 
                                    setShowDiviser(parcelle); 
                                    setFormDivision([
                                        { nom: 'Zone A', surface_m2: (parseFloat(parcelle.surface_m2) / 2).toFixed(1) },
                                        { nom: 'Zone B', surface_m2: (parseFloat(parcelle.surface_m2) / 2).toFixed(1) }
                                    ]);
                                }}
                                className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700"
                            >
                                <Scissors className="w-4 h-4" /> Diviser
                            </button>
                        </div>

                        {/* Cultures */}
                        <div className="mb-4">
                            <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                                <Leaf className="w-4 h-4" /> Cultures actives
                            </h4>
                            {parcelle.cultures.length === 0 ? (
                                <p className="text-gray-500 text-sm italic">Aucune culture active</p>
                            ) : (
                                <div className="space-y-2">
                                    {parcelle.cultures.map(culture => (
                                        <div key={culture.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                            <div className="flex items-center gap-3">
                                                <span className="text-2xl">{culture.icone || '🌱'}</span>
                                                <div>
                                                    <div className="font-medium">{culture.variete_nom}</div>
                                                    <div className="text-sm text-gray-500">
                                                        {culture.surface_m2} m² • {culture.statut} • 
                                                        Planté le {new Date(culture.date_reelle_semis || culture.date_reelle_repiquage).toLocaleDateString('fr-FR')}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => setShowEditCulture(culture)}
                                                    className="p-1.5 hover:bg-blue-100 rounded text-blue-600"
                                                    title="Modifier"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => setShowDeleteConfirm({ 
                                                        type: 'culture', 
                                                        id: culture.id, 
                                                        nom: culture.variete_nom 
                                                    })}
                                                    className="p-1.5 hover:bg-red-100 rounded text-red-600"
                                                    title="Supprimer"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Modules IoT */}
                        <div>
                            <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                                <Cpu className="w-4 h-4" /> Modules IoT
                            </h4>
                            {parcelle.modules_iot.length === 0 ? (
                                <p className="text-gray-500 text-sm italic">Aucun module associé</p>
                            ) : (
                                <div className="space-y-2">
                                    {parcelle.modules_iot.map(module => (
                                        <div key={module.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                            <div className="flex items-center gap-3">
                                                <span className={`w-3 h-3 rounded-full ${
                                                    module.etat_connexion === 'online' ? 'bg-green-500' :
                                                    module.etat_connexion === 'retard' ? 'bg-yellow-500' :
                                                    module.etat_connexion === 'offline' ? 'bg-red-500' :
                                                    'bg-gray-400'
                                                }`} />
                                                <div>
                                                    <div className="font-medium">{module.code_module}</div>
                                                    <div className="text-sm text-gray-500">
                                                        {module.nom} • {module.etat_connexion}
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleDissocierModule(parcelle.id, module.id)}
                                                className="p-1.5 hover:bg-red-100 rounded text-red-600"
                                                title="Dissocier"
                                            >
                                                <Unlink className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // ═══════════════════════════════════════════════════════════════════════
    // RENDU PRINCIPAL
    // ═══════════════════════════════════════════════════════════════════════
    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-green-600" />
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <MapPin className="w-7 h-7 text-green-600" />
                    Gestion des Parcelles
                </h1>
                <button
                    onClick={loadParcelles}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                    <RefreshCw className="w-4 h-4" /> Actualiser
                </button>
            </div>

            {/* Barre de recherche */}
            <div className="mb-6">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Rechercher une parcelle ou une culture..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                </div>
            </div>

            {/* Stats rapides */}
            <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-green-600">{parcelles.length}</div>
                    <div className="text-sm text-green-800">Parcelles</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-blue-600">
                        {parcelles.reduce((sum, p) => sum + p.cultures.length, 0)}
                    </div>
                    <div className="text-sm text-blue-800">Cultures actives</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-purple-600">
                        {parcelles.reduce((sum, p) => sum + p.modules_iot.length, 0)}
                    </div>
                    <div className="text-sm text-purple-800">Modules IoT</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-orange-600">
                        {parcelles.reduce((sum, p) => sum + parseFloat(p.surface_disponible || 0), 0).toFixed(0)} m²
                    </div>
                    <div className="text-sm text-orange-800">Surface disponible</div>
                </div>
            </div>

            {/* Liste des parcelles */}
            <div>
                {parcellesFiltrees.map(renderParcelle)}
            </div>

            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* MODAL AJOUTER CULTURE */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            {showAddCulture && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-lg w-full p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold flex items-center gap-2">
                                <Plus className="w-5 h-5 text-green-600" />
                                Ajouter une culture
                            </h3>
                            <button onClick={() => setShowAddCulture(null)} className="p-1 hover:bg-gray-100 rounded">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        
                        <div className="space-y-4">
                            {/* Famille */}
                            <div>
                                <label className="block text-sm font-medium mb-1">Famille</label>
                                <select
                                    value={formCulture.famille_id}
                                    onChange={(e) => {
                                        setFormCulture({ ...formCulture, famille_id: e.target.value, variete_id: '' });
                                        loadVarietes(e.target.value);
                                    }}
                                    className="w-full border rounded-lg p-2"
                                >
                                    <option value="">-- Choisir --</option>
                                    {familles.map(f => (
                                        <option key={f.id} value={f.id}>{f.nom}</option>
                                    ))}
                                </select>
                            </div>
                            
                            {/* Variété */}
                            {formCulture.famille_id && (
                                <div>
                                    <label className="block text-sm font-medium mb-1">Variété</label>
                                    <select
                                        value={formCulture.variete_id}
                                        onChange={(e) => setFormCulture({ ...formCulture, variete_id: e.target.value })}
                                        className="w-full border rounded-lg p-2"
                                    >
                                        <option value="">-- Choisir --</option>
                                        {varietes.map(v => (
                                            <option key={v.variete_id} value={v.variete_id}>
                                                {v.icone} {v.nom_complet}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            
                            {/* Date et type */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Date plantation</label>
                                    <input
                                        type="date"
                                        value={formCulture.date_plantation}
                                        onChange={(e) => setFormCulture({ ...formCulture, date_plantation: e.target.value })}
                                        className="w-full border rounded-lg p-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Type</label>
                                    <select
                                        value={formCulture.type_plantation}
                                        onChange={(e) => setFormCulture({ ...formCulture, type_plantation: e.target.value })}
                                        className="w-full border rounded-lg p-2"
                                    >
                                        <option value="semis">🌱 Semis</option>
                                        <option value="repiquage">🌿 Repiquage</option>
                                    </select>
                                </div>
                            </div>
                            
                            {/* Surface et plants */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Surface (m²)</label>
                                    <input
                                        type="number"
                                        value={formCulture.surface_m2}
                                        onChange={(e) => setFormCulture({ ...formCulture, surface_m2: e.target.value })}
                                        className="w-full border rounded-lg p-2"
                                        placeholder="Ex: 25"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Nb plants</label>
                                    <input
                                        type="number"
                                        value={formCulture.nb_plants}
                                        onChange={(e) => setFormCulture({ ...formCulture, nb_plants: e.target.value })}
                                        className="w-full border rounded-lg p-2"
                                    />
                                </div>
                            </div>
                            
                            <button
                                onClick={handleAddCulture}
                                disabled={!formCulture.variete_id || !formCulture.surface_m2}
                                className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
                            >
                                Ajouter la culture
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* MODAL CONFIRMATION SUPPRESSION */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-md w-full p-6">
                        <div className="text-center">
                            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                            <h3 className="text-lg font-bold mb-2">Confirmer la suppression</h3>
                            <p className="text-gray-600 mb-6">
                                Voulez-vous vraiment supprimer la culture<br />
                                <strong>{showDeleteConfirm.nom}</strong> ?
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowDeleteConfirm(null)}
                                    className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={handleDeleteCulture}
                                    className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                                >
                                    Supprimer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* MODAL ASSOCIER MODULE IoT */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            {showAssocierModule && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-md w-full p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold flex items-center gap-2">
                                <Cpu className="w-5 h-5 text-blue-600" />
                                Associer un module IoT
                            </h3>
                            <button onClick={() => setShowAssocierModule(null)} className="p-1 hover:bg-gray-100 rounded">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        
                        {modulesDisponibles.length === 0 ? (
                            <p className="text-gray-500 text-center py-4">Aucun module disponible</p>
                        ) : (
                            <div className="space-y-2">
                                {modulesDisponibles.map(module => (
                                    <button
                                        key={module.id}
                                        onClick={() => handleAssocierModule(module.id)}
                                        className="w-full p-3 border rounded-lg hover:bg-blue-50 text-left flex items-center gap-3"
                                    >
                                        <Cpu className="w-5 h-5 text-blue-600" />
                                        <div>
                                            <div className="font-medium">{module.code_module}</div>
                                            <div className="text-sm text-gray-500">{module.nom}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* MODAL DIVISER PARCELLE */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            {showDiviser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl max-w-lg w-full p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold flex items-center gap-2">
                                <Scissors className="w-5 h-5 text-purple-600" />
                                Diviser {showDiviser.nom}
                            </h3>
                            <button onClick={() => setShowDiviser(null)} className="p-1 hover:bg-gray-100 rounded">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-4">
                            Surface totale : <strong>{showDiviser.surface_m2} m²</strong>
                        </p>
                        
                        <div className="space-y-3 mb-4">
                            {formDivision.map((zone, idx) => (
                                <div key={idx} className="flex gap-2 items-center">
                                    <input
                                        type="text"
                                        value={zone.nom}
                                        onChange={(e) => {
                                            const newZones = [...formDivision];
                                            newZones[idx].nom = e.target.value;
                                            setFormDivision(newZones);
                                        }}
                                        className="flex-1 border rounded-lg p-2"
                                        placeholder="Nom de la zone"
                                    />
                                    <input
                                        type="number"
                                        value={zone.surface_m2}
                                        onChange={(e) => {
                                            const newZones = [...formDivision];
                                            newZones[idx].surface_m2 = e.target.value;
                                            setFormDivision(newZones);
                                        }}
                                        className="w-24 border rounded-lg p-2"
                                        placeholder="m²"
                                    />
                                    {formDivision.length > 2 && (
                                        <button
                                            onClick={() => setFormDivision(formDivision.filter((_, i) => i !== idx))}
                                            className="p-2 text-red-500 hover:bg-red-50 rounded"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        
                        <button
                            onClick={() => setFormDivision([...formDivision, { nom: `Zone ${String.fromCharCode(65 + formDivision.length)}`, surface_m2: '' }])}
                            className="w-full py-2 border-2 border-dashed rounded-lg text-gray-500 hover:border-purple-400 hover:text-purple-600 mb-4"
                        >
                            + Ajouter une zone
                        </button>
                        
                        <div className="text-sm text-gray-600 mb-4">
                            Total : {formDivision.reduce((sum, z) => sum + parseFloat(z.surface_m2 || 0), 0).toFixed(1)} m² 
                            / {showDiviser.surface_m2} m²
                        </div>
                        
                        <button
                            onClick={handleDiviser}
                            className="w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700"
                        >
                            Diviser la parcelle
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GestionParcelles;